import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
    TextField, Button, Stack, Typography, Alert, Box, Autocomplete, CircularProgress,
    MenuItem, Checkbox, FormControlLabel, Chip, Divider
} from '@mui/material';
import { crearTramiteReajuste, getChecklistFormularios, getTramitesReajuste } from '../../services/reajusteService';
import { buscarClientes, getClientePorCuil } from '../../services/clienteService';
import ModalRegistrarCliente from '../ModalRegistrarCliente';
import SeccionGenerarPacto from '../Pacto/SeccionGenerarPacto';
import usePactosPendientes from '../Pacto/usePactosPendientes';
import { useAuth } from '../../auth/AuthContext';
import { apellidoNombre } from '../../utils/nombrePersona';
import CampoFechaLiquidar from '../Tramite/CampoFechaLiquidar';
import InputMontoMui from '../InputMontoMui';
import PageHeader from '../ui/PageHeader';

function CampoMontoMui({ label, value, onChange, helperText, sx }) {
    return (
        <InputMontoMui label={label} value={value} onChange={onChange} fullWidth helperText={helperText} sx={sx} />
    );
}

/**
 * Formulario para iniciar un trámite del área Reajuste de Haberes. Plano (sin subtipos de
 * ciclo de vida): el ciclo de estados liviano avanza después, desde la ficha. El cálculo
 * (diferencias/retroactivo) es 100% manual — Jáuregui no se puede integrar.
 */
export default function FormularioIniciarTramiteReajuste({ onSuccess, onCancel, cuilInicial, idTurnoOrigen }) {
    const navigate = useNavigate();
    const { registrarPacto, adjuntarATramite } = usePactosPendientes();
    const { user } = useAuth();
    const { cuil: cuilParam } = useParams();
    const cuilEfectivo = cuilInicial || cuilParam;

    const [form, setForm] = useState({
        idCliente: '',
        fechaInicioEstimada: '',
        idUsuario: user?.id || null,
        beneficio: 'JUBILACION',
        tipoReajuste: 'COMUN',
        fechaAdquisicion: '',
        fechaReclamoAdministrativo: '',
        nroExpediente: '',
        // Datos del causante (solo aplica si beneficio=PENSION; el fallecido, NO es cliente).
        causanteApellidoNombre: '',
        causanteDniCuil: '',
        causanteFechaNacimiento: '',
        causanteFechaDefuncion: '',
        causanteDomicilio: '',
        causanteRelacionDependencia: '',
        causanteAutonomoMonotributo: '',
        causanteCuentaAfip: '',
        pbuReajustado: false,
        fechaAjustePbu: '',
        expedienteConseguido: false,
        expedienteDigitalizado: false,
        tiene150: false,
        resultado150: '',
        diferenciaConIpc: '',
        diferenciaSinIpc: '',
        retroactivoConIpc: '',
        retroactivoSinIpc: '',
        comentarios: '',
        // Renglones de la Carátula (ver components/Caratula). Se pueden cargar acá o más
        // tarde desde el modal de carátula. OBSERVACIONES no está: iría pegado al campo
        // "Observaciones" de abajo, que es otra cosa (tramite.comentarios, la nota firmada).
        turnoTomarVista: '',
        conceptosReclamo: '',
        indicePuro: '',
        ley: '',
    });
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');
    const [guardando, setGuardando] = useState(false);

    // --- Búsqueda de cliente ---
    const [clienteSeleccionado, setClienteSeleccionado] = useState(null);
    const [opcionesCliente, setOpcionesCliente] = useState([]);
    const [buscando, setBuscando] = useState(false);
    const [modalClienteOpen, setModalClienteOpen] = useState(false);
    const busquedaTimeoutRef = useRef(null);

    // --- Trámite de Reajuste duplicado para el mismo cliente (patrón RTI/ART/Jubilación/PUAM/Plan de Pago) ---
    const [tieneDuplicadoReajuste, setTieneDuplicadoReajuste] = useState(false);
    const [cantidadDuplicados, setCantidadDuplicados] = useState(0);
    const [cargandoDuplicados, setCargandoDuplicados] = useState(false);
    const [confirmarDuplicado, setConfirmarDuplicado] = useState(false);

    // --- Preview del checklist ---
    const [checklist, setChecklist] = useState([]);
    const [cargandoChecklist, setCargandoChecklist] = useState(true);

    // Carga inicial de cliente si viene por URL
    useEffect(() => {
        const cargar = async () => {
            if (!cuilEfectivo) return;
            try {
                const cliente = await getClientePorCuil(cuilEfectivo);
                setClienteSeleccionado(cliente);
                setForm(prev => ({ ...prev, idCliente: cliente.cuil }));
            } catch (err) {
                console.error('Error al cargar cliente inicial:', err);
            }
        };
        cargar();
    }, [cuilEfectivo]);

    // Preview en vivo del checklist: depende solo de tipoReajuste (resolver puro)
    useEffect(() => {
        let activo = true;
        const cargarChecklist = async () => {
            setCargandoChecklist(true);
            try {
                const data = await getChecklistFormularios(form.tipoReajuste);
                if (activo) setChecklist(Array.isArray(data) ? data : []);
            } catch (err) {
                console.error('Error al previsualizar checklist:', err);
                if (activo) setChecklist([]);
            } finally {
                if (activo) setCargandoChecklist(false);
            }
        };
        cargarChecklist();
        return () => { activo = false; };
    }, [form.tipoReajuste]);

    // Trámite de Reajuste ya existente para el cliente seleccionado
    useEffect(() => {
        const cuilCliente = form.idCliente;
        if (!cuilCliente) {
            setTieneDuplicadoReajuste(false);
            setCantidadDuplicados(0);
            setConfirmarDuplicado(false);
            return;
        }

        let activo = true;
        const verificarDuplicado = async () => {
            setCargandoDuplicados(true);
            try {
                const tramites = await getTramitesReajuste(cuilCliente);
                const items = Array.isArray(tramites) ? tramites : [];
                if (activo) {
                    setCantidadDuplicados(items.length);
                    setTieneDuplicadoReajuste(items.length > 0);
                    if (items.length === 0) {
                        setConfirmarDuplicado(false);
                    }
                }
            } catch (err) {
                console.error('Error validando trámites de Reajuste duplicados:', err);
                if (activo) {
                    setTieneDuplicadoReajuste(false);
                    setCantidadDuplicados(0);
                }
            } finally {
                if (activo) setCargandoDuplicados(false);
            }
        };

        verificarDuplicado();
        return () => { activo = false; };
    }, [form.idCliente]);

    const handleBusquedaCliente = (_event, value) => {
        if (!value) { setOpcionesCliente([]); setBuscando(false); return; }
        setBuscando(true);
        if (busquedaTimeoutRef.current) clearTimeout(busquedaTimeoutRef.current);
        busquedaTimeoutRef.current = setTimeout(async () => {
            try {
                const resultados = await buscarClientes(value);
                setOpcionesCliente(resultados);
            } catch (err) {
                console.error('Error al buscar clientes', err);
            } finally {
                setBuscando(false);
            }
        }, 300);
    };

    const handleSeleccionCliente = (_event, newValue) => {
        setClienteSeleccionado(newValue);
        setForm(prev => ({ ...prev, idCliente: newValue ? newValue.cuil : '' }));
    };

    const handleRegistroExitoso = (clienteNuevo) => {
        setClienteSeleccionado(clienteNuevo);
        setForm(prev => ({ ...prev, idCliente: clienteNuevo.cuil }));
        setModalClienteOpen(false);
    };

    const handleChange = (e) => {
        const { name, value } = e.target;
        setForm(prev => ({ ...prev, [name]: value }));
    };

    const handleCheck = (e) => {
        const { name, checked } = e.target;
        setForm(prev => ({ ...prev, [name]: checked }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!form.idCliente) { setError('Debe seleccionar un cliente.'); return; }
        if (tieneDuplicadoReajuste && !confirmarDuplicado) {
            setError('Debe confirmar que desea crear otro trámite de Reajuste para un cliente que ya tiene uno.');
            return;
        }
        setError('');
        setSuccess('');
        setGuardando(true);
        try {
            const payload = {
                idCliente: form.idCliente,
                idUsuario: form.idUsuario,
                organismo: 'ANSES',
                beneficio: form.beneficio,
                tipoReajuste: form.tipoReajuste,
                fechaAdquisicion: form.fechaAdquisicion || null,
                fechaReclamoAdministrativo: form.fechaReclamoAdministrativo || null,
                nroExpediente: form.nroExpediente,
                causanteApellidoNombre: form.beneficio === 'PENSION' ? (form.causanteApellidoNombre || null) : null,
                causanteDniCuil: form.beneficio === 'PENSION' ? (form.causanteDniCuil || null) : null,
                causanteFechaNacimiento: form.beneficio === 'PENSION' ? (form.causanteFechaNacimiento || null) : null,
                causanteFechaDefuncion: form.beneficio === 'PENSION' ? (form.causanteFechaDefuncion || null) : null,
                causanteDomicilio: form.beneficio === 'PENSION' ? (form.causanteDomicilio || null) : null,
                causanteRelacionDependencia: form.beneficio === 'PENSION' ? (form.causanteRelacionDependencia || null) : null,
                causanteAutonomoMonotributo: form.beneficio === 'PENSION' ? (form.causanteAutonomoMonotributo || null) : null,
                causanteCuentaAfip: form.beneficio === 'PENSION' ? (form.causanteCuentaAfip || null) : null,
                fechaAjustePbu: form.pbuReajustado ? (form.fechaAjustePbu || null) : null,
                expedienteConseguido: form.expedienteConseguido,
                expedienteDigitalizado: form.expedienteDigitalizado,
                tiene150: form.tiene150,
                resultado150: form.tiene150 ? (form.resultado150 || null) : null,
                diferenciaConIpc: form.diferenciaConIpc !== '' ? form.diferenciaConIpc : null,
                diferenciaSinIpc: form.diferenciaSinIpc !== '' ? form.diferenciaSinIpc : null,
                retroactivoConIpc: form.retroactivoConIpc !== '' ? form.retroactivoConIpc : null,
                retroactivoSinIpc: form.retroactivoSinIpc !== '' ? form.retroactivoSinIpc : null,
                turnoTomarVista: form.turnoTomarVista,
                conceptosReclamo: form.conceptosReclamo,
                indicePuro: form.indicePuro,
                ley: form.tipoReajuste === 'REPARACION_HISTORICA' ? form.ley : '',
                comentarios: form.comentarios,
                fechaInicioEstimada: form.fechaInicioEstimada || null,
                idTurnoOrigen: idTurnoOrigen || null,
            };
            const creado = await crearTramiteReajuste(payload);
            // El pacto se emitió antes de que el trámite existiera: recién ahora se puede adjuntar.
            await adjuntarATramite(creado.idTramite);
            setSuccess(`Trámite de Reajuste creado con éxito. ID: ${creado.idTramite}`);
            if (onSuccess) {
                onSuccess(creado);
            } else {
                navigate(`/reajuste/tramites/${creado.idTramite}`);
            }
        } catch (err) {
            setError(err.response?.data || err.response?.data?.message || 'Error al crear el trámite.');
        } finally {
            setGuardando(false);
        }
    };

    const seccionTitulo = (icono, texto) => (
        <div style={{
            background: 'var(--primary-bg)',
            padding: '0.75rem 1.25rem', borderRadius: 'var(--radius-lg)', border: '1px solid var(--primary-border)', marginTop: '0.5rem'
        }}>
            <Typography variant="h6" fontWeight={600} sx={{ color: 'var(--primary-text)', fontSize: 'var(--fs-texto-lg)' }}>
                <i className={`bi ${icono} me-2`}></i>{texto}
            </Typography>
        </div>
    );

    return (
        <div className="area-rti">
            <Box sx={{ background: 'var(--bg-page)', minHeight: '100vh', padding: 3 }}>
                <Box sx={{ maxWidth: 900, margin: 'auto', background: 'white', borderRadius: '16px', boxShadow: 'var(--shadow-md)', overflow: 'hidden' }}>
                    <PageHeader
                        variante="banner"
                        icono="graph-up-arrow"
                        titulo="Iniciar Trámite de Reajuste de Haberes"
                        descripcion="Complete los datos para registrar un nuevo trámite de reajuste ante ANSES"
                    />

                    <Box sx={{ padding: '2rem' }}>
                    <ModalRegistrarCliente open={modalClienteOpen} onClose={() => setModalClienteOpen(false)} onSuccess={handleRegistroExitoso} mostrarDialogoConfirmacion={false} />

                    <form onSubmit={handleSubmit}>
                        <Stack spacing={3}>
                            {/* Cliente */}
                            {seccionTitulo('bi-person-check', 'Seleccionar Cliente')}
                            <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-start' }}>
                                <Autocomplete
                                    id="buscar-cliente-reajuste"
                                    options={opcionesCliente}
                                    getOptionLabel={(option) => `${apellidoNombre(option.apellido, option.nombre)} (${option.cuil})`}
                                    onInputChange={handleBusquedaCliente}
                                    onChange={handleSeleccionCliente}
                                    value={clienteSeleccionado}
                                    isOptionEqualToValue={(option, value) => option.cuil === value.cuil}
                                    noOptionsText="No se encontraron clientes"
                                    loading={buscando}
                                    loadingText="Buscando..."
                                    sx={{ flexGrow: 1 }}
                                    renderInput={(params) => (
                                        <TextField
                                            {...params}
                                            label="Buscar cliente por CUIL, Apellido o Nombre"
                                            InputProps={{
                                                ...params.InputProps,
                                                endAdornment: (
                                                    <>
                                                        {buscando ? <CircularProgress color="inherit" size={20} /> : null}
                                                        {params.InputProps.endAdornment}
                                                    </>
                                                ),
                                            }}
                                        />
                                    )}
                                />
                                <Button variant="outlined" onClick={() => setModalClienteOpen(true)} sx={{ height: '56px', flexShrink: 0 }}>
                                    Registrar Cliente
                                </Button>
                            </Box>

                            <SeccionGenerarPacto cliente={clienteSeleccionado} mostrarBadaro onPactoGenerado={registrarPacto} />

                            {/* Datos del beneficio */}
                            {seccionTitulo('bi-file-earmark-text', 'Datos del Beneficio')}

                            <TextField label="Organismo" value="ANSES" fullWidth disabled
                                sx={{ '& .MuiInputBase-input.Mui-disabled': { WebkitTextFillColor: '#000', color: '#000' } }} />

                            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                                <TextField select label="Beneficio" name="beneficio" value={form.beneficio} onChange={handleChange} fullWidth>
                                    <MenuItem value="JUBILACION">Jubilación</MenuItem>
                                    <MenuItem value="PENSION">Pensión</MenuItem>
                                </TextField>
                                <TextField select label="Tipo de Reajuste" name="tipoReajuste" value={form.tipoReajuste} onChange={handleChange} fullWidth
                                    helperText="Condiciona la documentación requerida.">
                                    <MenuItem value="COMUN">Común</MenuItem>
                                    <MenuItem value="REPARACION_HISTORICA">Reparación Histórica</MenuItem>
                                    <MenuItem value="DOCENTE">Docente</MenuItem>
                                    <MenuItem value="OTRO">Otro</MenuItem>
                                </TextField>
                            </Stack>

                            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                                <TextField type="date" label="Fecha de adquisición del beneficio" name="fechaAdquisicion"
                                    value={form.fechaAdquisicion} onChange={handleChange} fullWidth InputLabelProps={{ shrink: true }} />
                                <TextField type="date" label="Fecha de reclamo administrativo" name="fechaReclamoAdministrativo"
                                    value={form.fechaReclamoAdministrativo} onChange={handleChange} fullWidth InputLabelProps={{ shrink: true }}
                                    helperText="Define el 1er día no prescripto." />
                            </Stack>

                            <TextField label="N° de expediente del beneficio" name="nroExpediente" helperText="El del beneficio que se reajusta (no se sigue). El del reclamo sale del turno ANSES." value={form.nroExpediente} onChange={handleChange} fullWidth />

                            {form.beneficio === 'PENSION' && (
                                <>
                                    {seccionTitulo('bi-person-x', 'Datos del Causante')}
                                    <TextField label="Apellido y Nombre del causante" name="causanteApellidoNombre" value={form.causanteApellidoNombre} onChange={handleChange} fullWidth />
                                    <TextField label="DNI/CUIL del causante" name="causanteDniCuil" value={form.causanteDniCuil} onChange={handleChange} fullWidth />
                                    <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                                        <TextField type="date" label="Fecha de nacimiento" name="causanteFechaNacimiento" value={form.causanteFechaNacimiento} onChange={handleChange} fullWidth InputLabelProps={{ shrink: true }} />
                                        <TextField type="date" label="Fecha de defunción" name="causanteFechaDefuncion" value={form.causanteFechaDefuncion} onChange={handleChange} fullWidth InputLabelProps={{ shrink: true }} />
                                    </Stack>
                                    <TextField label="Domicilio del causante" name="causanteDomicilio" value={form.causanteDomicilio} onChange={handleChange} fullWidth helperText="Tal como figura en el acta de defunción." />
                                    <TextField label="Relación de dependencia" name="causanteRelacionDependencia" value={form.causanteRelacionDependencia} onChange={handleChange} fullWidth helperText='Por ejemplo: "4 años y 10 meses".' />
                                    <TextField select label="Autónomo o monotributo" name="causanteAutonomoMonotributo" value={form.causanteAutonomoMonotributo} onChange={handleChange} fullWidth>
                                        <MenuItem value=""><em>Sin especificar</em></MenuItem>
                                        <MenuItem value="AUTONOMO">Autónomo</MenuItem>
                                        <MenuItem value="MONOTRIBUTO">Monotributo</MenuItem>
                                    </TextField>
                                    <TextField label="Cuenta AFIP" name="causanteCuentaAfip" value={form.causanteCuentaAfip} onChange={handleChange} fullWidth />
                                </>
                            )}

                            <FormControlLabel
                                control={<Checkbox name="expedienteConseguido" checked={form.expedienteConseguido} onChange={handleCheck} />}
                                label="Expediente conseguido"
                            />

                            <FormControlLabel
                                control={<Checkbox name="expedienteDigitalizado" checked={form.expedienteDigitalizado} onChange={handleCheck} />}
                                label="El expediente está digitalizado"
                            />

                            <FormControlLabel
                                control={<Checkbox name="pbuReajustado" checked={form.pbuReajustado} onChange={handleCheck} />}
                                label="PBU reajustado"
                            />
                            {form.pbuReajustado && (
                                <TextField type="date" label="Fecha de ajuste del PBU" name="fechaAjustePbu"
                                    value={form.fechaAjustePbu} onChange={handleChange} InputLabelProps={{ shrink: true }} sx={{ maxWidth: 300 }} />
                            )}

                            <FormControlLabel
                                control={<Checkbox name="tiene150" checked={form.tiene150} onChange={handleCheck} />}
                                label='Se abrió el expediente "150" (para el retroactivo)'
                            />
                            {form.tiene150 && (
                                <TextField select label="Resultado del 150" name="resultado150" value={form.resultado150} onChange={handleChange}
                                    sx={{ maxWidth: 300 }}>
                                    <MenuItem value="PENDIENTE">Pendiente</MenuItem>
                                    <MenuItem value="FAVORABLE">Favorable</MenuItem>
                                    <MenuItem value="DESFAVORABLE">Desfavorable</MenuItem>
                                </TextField>
                            )}

                            {/* Datos de la Carátula: los renglones del documento que no tienen
                                dónde vivir en el resto de la ficha. Se pueden dejar en blanco y
                                completarlos después desde el botón "Generar Carátula". */}
                            {seccionTitulo('bi-file-earmark-text', 'Datos de la Carátula')}
                            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                                <TextField label="Turno para tomar vista" name="turnoTomarVista"
                                    value={form.turnoTomarVista} onChange={handleChange} fullWidth
                                    helperText="Texto libre: puede ser una fecha o &quot;a coordinar&quot;." />
                                <TextField label="Incide puro N°" name="indicePuro"
                                    value={form.indicePuro} onChange={handleChange} fullWidth />
                            </Stack>
                            <TextField label="Conceptos del reclamo" name="conceptosReclamo"
                                value={form.conceptosReclamo} onChange={handleChange} multiline rows={2} fullWidth />
                            {form.tipoReajuste === 'REPARACION_HISTORICA' && (
                                <TextField label="Ley" name="ley" value={form.ley} onChange={handleChange}
                                    sx={{ maxWidth: 300 }}
                                    helperText="Solo se imprime en la carátula de Reparación Histórica." />
                            )}

                            {/* Cálculo manual */}
                            {seccionTitulo('bi-calculator', 'Cálculo (manual)')}
                            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                                <CampoMontoMui label="Diferencia CON IPC" value={form.diferenciaConIpc}
                                    onChange={(v) => setForm(p => ({ ...p, diferenciaConIpc: v }))} />
                                <CampoMontoMui label="Diferencia SIN IPC" value={form.diferenciaSinIpc}
                                    onChange={(v) => setForm(p => ({ ...p, diferenciaSinIpc: v }))} />
                            </Stack>
                            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                                <CampoMontoMui label="Retroactivo con IPC" value={form.retroactivoConIpc}
                                    onChange={(v) => setForm(p => ({ ...p, retroactivoConIpc: v }))} />
                                <CampoMontoMui label="Retroactivo sin IPC" value={form.retroactivoSinIpc}
                                    onChange={(v) => setForm(p => ({ ...p, retroactivoSinIpc: v }))} />
                            </Stack>
                            {/* Retroactivo pendiente y Adelanto NO van acá: recién hay datos reales
                                cuando el reclamo pasa a instancia judicial, se cargan en ese momento
                                desde el mini-formulario de esa transición (en la ficha). */}

                            <CampoFechaLiquidar
                                valor={form.fechaInicioEstimada}
                                onChange={(v) => setForm((f) => ({ ...f, fechaInicioEstimada: v }))}
                            />

                            <TextField label="Observaciones" name="comentarios" value={form.comentarios} onChange={handleChange} multiline rows={4} fullWidth
                                helperText="Particularidades del caso (lo que se le deja anotado al equipo)." />

                            {/* Checklist en vivo */}
                            <Box sx={{ background: '#eff6ff', border: '1px solid #bfdbfe', borderRadius: '12px', p: 2 }}>
                                <Typography variant="subtitle1" fontWeight={600} color="#1e40af" sx={{ mb: 1 }}>
                                    <i className="bi bi-check2-square me-2"></i>
                                    Documentación requerida {cargandoChecklist && <CircularProgress size={14} sx={{ ml: 1 }} />}
                                </Typography>
                                <Divider sx={{ mb: 1 }} />
                                {checklist.length === 0 && !cargandoChecklist ? (
                                    <Typography variant="body2" color="text.secondary">Sin documentación configurada.</Typography>
                                ) : (
                                    <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap">
                                        {checklist.map(f => <Chip key={f.codigo} label={f.nombre} size="small" color="primary" variant="outlined" />)}
                                    </Stack>
                                )}
                            </Box>

                            {error && <Alert severity="error" sx={{ borderRadius: '10px' }}>{String(error)}</Alert>}
                            {success && <Alert severity="success" sx={{ borderRadius: '10px' }}>{success}</Alert>}
                            {tieneDuplicadoReajuste && (
                                <Alert severity="warning" sx={{ borderRadius: '10px' }}>
                                    Este cliente ya tiene {cantidadDuplicados === 1 ? 'un trámite de Reajuste' : `${cantidadDuplicados} trámites de Reajuste`}. ¿Está seguro de que desea crear otro?
                                </Alert>
                            )}
                            {tieneDuplicadoReajuste && (
                                <FormControlLabel
                                    control={
                                        <Checkbox
                                            checked={confirmarDuplicado}
                                            onChange={(e) => setConfirmarDuplicado(e.target.checked)}
                                        />
                                    }
                                    label="Confirmo que deseo continuar con un nuevo trámite de Reajuste igualmente."
                                />
                            )}
                            {cargandoDuplicados && <Alert severity="info" sx={{ borderRadius: '10px' }}>Verificando trámites de Reajuste activos del cliente...</Alert>}

                            <Stack direction="row" spacing={2} sx={{ width: '100%', marginTop: '1rem' }}>
                                <Button fullWidth variant="outlined" size="large"
                                    onClick={() => onCancel ? onCancel() : navigate('/tramites')}
                                    sx={{ color: '#dc2626', border: '2px solid #fecaca', borderRadius: '10px', textTransform: 'none' }}>
                                    <i className="bi bi-x-circle me-2"></i>Cancelar
                                </Button>
                                <Button fullWidth type="submit" size="large" disabled={!form.idCliente || guardando}
                                    sx={{
                                        background: 'var(--primary)', color: 'white', borderRadius: '10px',
                                        textTransform: 'none', fontWeight: 600,
                                        '&:hover': { background: 'var(--primary)' },
                                        '&:disabled': { background: '#e5e7eb', color: '#9ca3af' }
                                    }}>
                                    {guardando ? <CircularProgress size={20} sx={{ color: 'white' }} /> : <><i className="bi bi-check-circle me-2"></i>Iniciar Trámite</>}
                                </Button>
                            </Stack>
                        </Stack>
                    </form>
                </Box>
            </Box>
        </Box>
    </div>
);
}
